/**
 * @description Override vuetify theme css variables with easy theme css variables in the correct format
 */
export const overrideVuetifyTheme = () => {
  const themeCssSelector = ".v-theme--easyTheme[class^='v-']";
  const overrideStyleElId = "vuetify-theme-override";

  const vuetifyVsEasyVariablesMapper: { [vuetifyVarKey: string]: string | number } = {
    "--v-theme-background": "--easy__color--back",
    "--v-theme-background-overlay-multiplier": 1,
    "--v-theme-surface": "--easy__color--front",
    "--v-theme-surface-overlay-multiplier": 1,
    "--v-theme-surface-bright": "--easy__color--front",
    "--v-theme-surface-bright-overlay-multiplier": 1,
    "--v-theme-surface-light": "--easy__color--front",
    "--v-theme-surface-light-overlay-multiplier": 1,
    "--v-theme-surface-variant": "--easy__color--text",
    "--v-theme-surface-variant-overlay-multiplier": 2,
    "--v-theme-on-surface-variant": "--easy__color--front",
    "--v-theme-primary": "--easy__color--main",
    "--v-theme-primary-overlay-multiplier": 2,
    "--v-theme-primary-darken-1": "--easy__color--main-110",
    "--v-theme-primary-darken-1-overlay-multiplier": 2,
    "--v-theme-secondary": "--easy__color--positive",
    "--v-theme-secondary-overlay-multiplier": 1,
    "--v-theme-secondary-darken-1": "--easy__color--positive-110",
    "--v-theme-secondary-darken-1-overlay-multiplier": 1,
    "--v-theme-error": "--easy__color--negative",
    "--v-theme-error-overlay-multiplier": 2,
    "--v-theme-info": "--easy__notice-background",
    "--v-theme-info-overlay-multiplier": 1,
    "--v-theme-success": "--easy__color--positive",
    "--v-theme-success-overlay-multiplier": 1,
    "--v-theme-warning": "--easy__color--important",
    "--v-theme-warning-overlay-multiplier": 1,
    "--v-theme-on-background": "--easy__color--text",
    "--v-theme-on-surface": "--easy__color--text",
    "--v-theme-on-surface-bright": "--easy__color--text",
    "--v-theme-on-surface-light": "--easy__color--text",
    "--v-theme-on-primary": "--easy__color--text-inverted",
    "--v-theme-on-primary-darken-1": "--easy__color--text-inverted",
    "--v-theme-on-secondary": "--easy__color--text",
    "--v-theme-on-secondary-darken-1": "--easy__color--text-inverted",
    "--v-theme-on-error": "--easy__color--text-inverted",
    "--v-theme-on-info": "--easy__color--text-inverted",
    "--v-theme-on-success": "--easy__color--text-inverted",
    "--v-theme-on-warning": "--easy__color--text",
    "--v-border-color": "--easy__color--border",
    "--v-border-opacity": 0.12,
    "--v-high-emphasis-opacity": 1,
    "--v-medium-emphasis-opacity": 1,
    "--v-disabled-opacity": 0.38,
    "--v-idle-opacity": 0.04,
    "--v-hover-opacity": 0.04,
    "--v-focus-opacity": 0.12,
    "--v-selected-opacity": 0.08,
    "--v-activated-opacity": 0.12,
    "--v-pressed-opacity": 0.12,
    "--v-dragged-opacity": 0.08,
    "--v-theme-kbd": "--easy__color--text-muted",
    "--v-theme-on-kbd": "--easy__color--text-inverted",
    "--v-theme-code": "#C7254E",
    "--v-theme-on-code": "0, 0, 0",
    "--v-theme-muted": "--easy__color--text-muted",
  };

  const vuetifyVsEasyVariables = Object.keys(vuetifyVsEasyVariablesMapper).reduce((variables, vuetifyVarKey) => {
    const newValue = vuetifyVsEasyVariablesMapper[vuetifyVarKey];
    if (typeof newValue === "string" && newValue.startsWith("--easy")) {
      variables += `${vuetifyVarKey}: ${getRGBFromCssVar(newValue)};`;
    } else if (typeof newValue === "string" && newValue.startsWith("#")) {
      variables += `${vuetifyVarKey}: ${getRGBFromHex(newValue)};`;
    } else {
      variables += `${vuetifyVarKey}: ${newValue};`;
    }
    return variables;
  }, "");

  const overrideStyle = `${themeCssSelector} { ${vuetifyVsEasyVariables} }`;

  if (document.getElementById(overrideStyleElId)) {
    document.getElementById(overrideStyleElId)!.innerHTML = overrideStyle;
  } else {
    const styleElement = document.createElement("style");
    styleElement.id = overrideStyleElId;
    styleElement.innerHTML = overrideStyle;
    document.head.appendChild(styleElement);
  }
};

/**
 * Get rgb color value from css variable with hex color
 * @param cssVar
 */
const getRGBFromCssVar = (cssVar: string) => {
  const easyVarValue = window.getComputedStyle(document.documentElement).getPropertyValue(cssVar);
  if (easyVarValue.startsWith("#")) {
    return getRGBFromHex(easyVarValue);
  }

  if (easyVarValue.startsWith("rgb")) {
    return getRGBFromRGBString(easyVarValue);
  }

  return easyVarValue;
};

/**
 * @description Converts a six digit hex color to rgb
 * @param hex
 */
const getRGBFromHex = (hex: string) => {
  const fullHexLength = 6;
  const shortHexLength = 3;
  hex = hex.replace("#", "");

  if (hex.length === shortHexLength) {
    hex = convertShortHexToFullHex(hex);
  }
  if (hex.length !== fullHexLength) return hex;
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `${r},${g},${b}`;
};

const getRGBFromRGBString = (rgb: string) => {
  return rgb.replace("rgb(", "").replace(")", "");
};

const convertShortHexToFullHex = (hex: string) => {
  return hex
    .split("")
    .map(char => char + char)
    .join("");
};
